/**
 * @fileoverview Recoverable Transactions — Overview snapshot
 * @module jobs/phases/overview/investigations/RecoverableTransactionsSnapshot
 *
 * Purpose: Compact snapshot of the recoverable (voidable) transactions for the
 * Investigations tab. Only shows flags the practitioner has approved — i.e.
 * decided "accepted" or "partially accepted" — and says so explicitly, so the
 * Overview never implies an un-reviewed agent output is settled. "See details"
 * opens the Recoverable Transactions workspace.
 *
 * Dependencies:
 * - Convex: flaggedTransactions.publicQueries.listFlags
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import type { FunctionReturnType } from "convex/server";
import { Flag } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { fmtMoney } from "../../review/flaggedTransactions/flaggedShared";
import { InvestigationSnapshotCard } from "./InvestigationSnapshotCard";

interface RecoverableTransactionsSnapshotProps {
  jobId: Id<"jobs">;
  onSeeDetails: () => void;
}

type FlagRow = FunctionReturnType<
  typeof api.jobs.summarization.agents.flaggedTransactions.publicQueries.listFlags
>[number];

/** Practitioner decisions that count as "approved" for the Overview. */
const APPROVED_BADGE: Partial<
  Record<FlagRow["decision"], { label: string; color: BadgeColor }>
> = {
  accepted: { label: "Accepted", color: "emerald" },
  partially_accepted: { label: "Partially accepted", color: "amber" },
};

/**
 * Approved recoverable transactions snapshot for the Investigations tab.
 *
 * @component
 */
export function RecoverableTransactionsSnapshot({
  jobId,
  onSeeDetails,
}: RecoverableTransactionsSnapshotProps) {
  const { data, isPending, isError } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries.listFlags,
      { jobId },
    ),
  );
  const approved = (data ?? []).filter(
    (row) =>
      row.decision === "accepted" || row.decision === "partially_accepted",
  );
  const approvedValue = approved.reduce(
    (sum, row) => sum + (row.quantum ?? row.amount),
    0,
  );

  return (
    <InvestigationSnapshotCard
      icon={Flag}
      title="Recoverable Transactions"
      onSeeDetails={onSeeDetails}
    >
      <div className="px-5 py-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            Showing approved transactions only
          </span>
          {approved.length > 0 && (
            <span className="text-xs">
              <span className="text-muted-foreground">Total: </span>
              <span className="font-semibold tabular-nums">
                {fmtMoney(approvedValue)}
              </span>
            </span>
          )}
        </div>
        {isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load recoverable transactions.
          </p>
        ) : approved.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No recoverable transactions have been approved yet.
          </p>
        ) : (
          <ul className="flex max-h-72 flex-col divide-y divide-border overflow-auto rounded-lg border border-border">
            {approved.map((row) => {
              const badge = APPROVED_BADGE[row.decision];
              return (
                <li
                  key={row.flagId}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">
                      {row.counterpartyName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {row.headLabel}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-semibold tabular-nums">
                      {fmtMoney(row.quantum ?? row.amount)}
                    </span>
                    {badge && (
                      <Badge color={badge.color} variant="soft" size="sm">
                        {badge.label}
                      </Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </InvestigationSnapshotCard>
  );
}
